import { useState, useEffect, type SyntheticEvent } from 'react';
import { getMe, updateMe, type Randonneur, type UpdateRandonneurInput } from '@/services/randonneur.service';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Toast from '@/components/common/Toast';
import { MdEdit, MdSave, MdClose, MdRefresh } from 'react-icons/md';
import axios from 'axios';

const STATUS_STYLES: Record<string, string> = {
  ativo: 'bg-brand-100 text-brand-800',
  suspenso: 'bg-yellow-100 text-yellow-800',
  inativo: 'bg-gray-100 text-gray-700',
  banido: 'bg-red-100 text-red-800',
};

type EditForm = {
  nome: string;
  sobrenome: string;
  telefone: string;
  data_nascimento: string;
  sexo: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  pais: string;
};

function toEditForm(r: Randonneur): EditForm {
  return {
    nome: r.nome ?? '',
    sobrenome: r.sobrenome ?? '',
    telefone: r.telefone ?? '',
    data_nascimento: r.data_nascimento ? r.data_nascimento.split('T')[0] : '',
    sexo: r.sexo ?? '',
    cep: r.cep ?? '',
    logradouro: r.logradouro ?? '',
    numero: r.numero ?? '',
    complemento: r.complemento ?? '',
    bairro: r.bairro ?? '',
    cidade: r.cidade ?? '',
    estado: r.estado ?? '',
    pais: r.pais ?? 'Brasil',
  };
}

export default function PerfilPage() {
  const [randonneur, setRandonneur] = useState<Randonneur | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof EditForm, string>>>({});
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' as 'success' | 'error' });

  const load = async () => {
    setIsLoading(true);
    setIsNetworkError(false);
    setIsNotFound(false);
    try {
      const data = await getMe();
      setRandonneur(data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setIsNotFound(true);
      } else {
        setIsNetworkError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = () => {
    if (!randonneur) return;
    setForm(toEditForm(randonneur));
    setFormErrors({});
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm(null);
    setFormErrors({});
  };

  const validate = (f: EditForm): Partial<Record<keyof EditForm, string>> => {
    const errs: Partial<Record<keyof EditForm, string>> = {};
    if (!f.nome.trim()) errs.nome = 'Nome é obrigatório';
    if (!f.sobrenome.trim()) errs.sobrenome = 'Sobrenome é obrigatório';
    return errs;
  };

  const handleSave = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!form || !randonneur) return;

    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }
    setFormErrors({});

    const payload: UpdateRandonneurInput = {};
    (Object.keys(form) as (keyof EditForm)[]).forEach((key) => {
      const val = form[key].trim();
      payload[key] = val || undefined;
    });
    // nome and sobrenome are required — always include them
    payload.nome = form.nome.trim();
    payload.sobrenome = form.sobrenome.trim();

    try {
      setIsSaving(true);
      const updated = await updateMe(randonneur.id, payload);
      setRandonneur(updated);
      setIsEditing(false);
      setForm(null);
      setToast({ visible: true, message: 'Dados atualizados com sucesso', type: 'success' });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const msg = (err.response.data as { erro?: string })?.erro ?? 'Erro de validação';
        setToast({ visible: true, message: msg, type: 'error' });
      } else {
        setToast({ visible: true, message: 'Não foi possível salvar. Tente novamente.', type: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const setField = (key: keyof EditForm, value: string) => {
    setForm((prev) => prev ? { ...prev, [key]: value } : prev);
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isNetworkError) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-600 mb-4">Não foi possível carregar seus dados. Verifique sua conexão.</p>
        <button onClick={load} className="btn-primary inline-flex items-center gap-2">
          <MdRefresh className="text-lg" />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (isNotFound) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-700 text-lg font-medium mb-2">Cadastro em análise</p>
        <p className="text-gray-500">
          Seu cadastro de randonneur está sendo processado. Entre em contato com seu clube.
        </p>
      </div>
    );
  }

  if (!randonneur) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast((t) => ({ ...t, visible: false }))}
      />

      {/* Member card */}
      <div className="card mb-6 bg-gradient-to-r from-brand-700 to-brand-600 text-white">
        <p className="text-brand-200 text-xs uppercase tracking-widest mb-1">Número de membro RandoBR</p>
        <p className="text-4xl font-bold tracking-wider">{randonneur.randobr_id ?? '—'}</p>
        <div className="mt-3 flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[randonneur.status] ?? 'bg-gray-100 text-gray-700'}`}>
            {randonneur.status}
          </span>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Meus dados</h2>
          {!isEditing && (
            <button onClick={startEdit} className="btn-primary flex items-center gap-2 text-sm">
              <MdEdit />
              Editar
            </button>
          )}
        </div>

        {isEditing && form ? (
          <form onSubmit={handleSave} className="space-y-5">
            {/* Nome + Sobrenome */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="perfil-nome" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input id="perfil-nome" type="text" value={form.nome} onChange={(e) => setField('nome', e.target.value)}
                  className="input-field" disabled={isSaving} />
                {formErrors.nome && <p className="mt-1 text-xs text-red-600">{formErrors.nome}</p>}
              </div>
              <div>
                <label htmlFor="perfil-sobrenome" className="block text-sm font-medium text-gray-700 mb-1">
                  Sobrenome <span className="text-red-500">*</span>
                </label>
                <input id="perfil-sobrenome" type="text" value={form.sobrenome} onChange={(e) => setField('sobrenome', e.target.value)}
                  className="input-field" disabled={isSaving} />
                {formErrors.sobrenome && <p className="mt-1 text-xs text-red-600">{formErrors.sobrenome}</p>}
              </div>
            </div>

            {/* Telefone + Data de nascimento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="perfil-telefone" className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input id="perfil-telefone" type="tel" value={form.telefone} onChange={(e) => setField('telefone', e.target.value)}
                  className="input-field" disabled={isSaving} placeholder="(11) 99999-9999" />
              </div>
              <div>
                <label htmlFor="perfil-data-de-nascimento" className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento</label>
                <input id="perfil-data-de-nascimento" type="date" value={form.data_nascimento} onChange={(e) => setField('data_nascimento', e.target.value)}
                  className="input-field" disabled={isSaving} />
              </div>
            </div>

            {/* Sexo */}
            <div>
              <label htmlFor="perfil-sexo" className="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
              <select id="perfil-sexo" value={form.sexo} onChange={(e) => setField('sexo', e.target.value)}
                className="input-field" disabled={isSaving}>
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="O">Outro</option>
              </select>
            </div>

            {/* Endereço */}
            <div className="border-t pt-5">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Endereço</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="perfil-cep" className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input id="perfil-cep" type="text" value={form.cep} onChange={(e) => setField('cep', e.target.value)}
                    className="input-field" disabled={isSaving} placeholder="00000-000" maxLength={9} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="perfil-logradouro" className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                  <input id="perfil-logradouro" type="text" value={form.logradouro} onChange={(e) => setField('logradouro', e.target.value)}
                    className="input-field" disabled={isSaving} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div>
                  <label htmlFor="perfil-numero" className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input id="perfil-numero" type="text" value={form.numero} onChange={(e) => setField('numero', e.target.value)}
                    className="input-field" disabled={isSaving} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="perfil-complemento" className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input id="perfil-complemento" type="text" value={form.complemento} onChange={(e) => setField('complemento', e.target.value)}
                    className="input-field" disabled={isSaving} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div>
                  <label htmlFor="perfil-bairro" className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input id="perfil-bairro" type="text" value={form.bairro} onChange={(e) => setField('bairro', e.target.value)}
                    className="input-field" disabled={isSaving} />
                </div>
                <div>
                  <label htmlFor="perfil-cidade" className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input id="perfil-cidade" type="text" value={form.cidade} onChange={(e) => setField('cidade', e.target.value)}
                    className="input-field" disabled={isSaving} />
                </div>
                <div>
                  <label htmlFor="perfil-estado" className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input id="perfil-estado" type="text" value={form.estado} onChange={(e) => setField('estado', e.target.value)}
                    className="input-field" disabled={isSaving} maxLength={2} placeholder="SP" />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="perfil-pais" className="block text-sm font-medium text-gray-700 mb-1">País</label>
                <input id="perfil-pais" type="text" value={form.pais} onChange={(e) => setField('pais', e.target.value)}
                  className="input-field" disabled={isSaving} />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={isSaving} className="btn-primary flex items-center gap-2">
                {isSaving ? <LoadingSpinner size="sm" /> : <MdSave />}
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
              <button type="button" onClick={cancelEdit} disabled={isSaving} className="btn-secondary flex items-center gap-2">
                <MdClose />
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          <dl className="space-y-4">
            <Row label="Nome" value={randonneur.nome} />
            <Row label="Sobrenome" value={randonneur.sobrenome} />
            <Row label="Telefone" value={randonneur.telefone} />
            <Row label="Data de nascimento" value={randonneur.data_nascimento ? randonneur.data_nascimento.split('T')[0] : null} />
            <Row label="Sexo" value={sexoPorExtenso(randonneur.sexo)} />

            <div className="border-t pt-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Endereço</p>
              <Row label="CEP" value={randonneur.cep} />
              <Row label="Logradouro" value={randonneur.logradouro} />
              <Row label="Número" value={randonneur.numero} />
              <Row label="Complemento" value={randonneur.complemento} />
              <Row label="Bairro" value={randonneur.bairro} />
              <Row label="Cidade" value={randonneur.cidade} />
              <Row label="Estado" value={randonneur.estado} />
              <Row label="País" value={randonneur.pais} />
            </div>

            <div className="border-t pt-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Dados da federação</p>
              <Row label="E-mail" value={randonneur.email} readOnly note="Para alterar, entre em contato com seu clube" />
              <Row label="CPF" value={randonneur.cpf} readOnly note="Para alterar, entre em contato com seu clube" />
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}

/** Códigos gravados no cadastro; qualquer outro valor aparece como veio. */
const SEXO_POR_EXTENSO: Record<string, string> = { M: 'Masculino', F: 'Feminino' };

function sexoPorExtenso(sexo: string | null): string | null {
  if (sexo === null) return null;
  return SEXO_POR_EXTENSO[sexo] ?? sexo;
}

function Row({ label, value, readOnly = false, note }: Readonly<{
  label: string;
  value: string | null | undefined;
  readOnly?: boolean;
  note?: string;
}>) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4">
      <dt className="text-sm font-medium text-gray-500 sm:w-40 flex-shrink-0">{label}</dt>
      <dd className="text-sm text-gray-800 mt-0.5 sm:mt-0">
        {value || <span className="text-gray-400 italic">Não informado</span>}
        {readOnly && note && (
          <span className="ml-2 text-xs text-gray-400">({note})</span>
        )}
      </dd>
    </div>
  );
}
